"use client";

import { useTranslations } from "use-intl";

import { OfferIcon, TrendingDownIcon } from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import { Link, type LinkTarget } from "@/components/link";
import { cn } from "@/lib/utils";

export type OfferSortMode = "deals" | "best_price";

interface OfferSortSelectorProps {
  value: OfferSortMode | null;
  onChange?: (value: OfferSortMode) => void;
  getLinkTarget?: (value: OfferSortMode) => LinkTarget;
  className?: string;
}

const OPTIONS: Array<{
  value: OfferSortMode;
  labelKey: "sortBestDeals" | "sortBestPrice";
  icon: AppIcon;
  activeClassName: string;
  hoverClassName: string;
}> = [
  {
    value: "deals",
    labelKey: "sortBestDeals",
    icon: TrendingDownIcon,
    activeClassName: "border-yellow-300 text-yellow-300",
    hoverClassName: "hover:border-yellow-400 hover:text-yellow-300",
  },
  {
    value: "best_price",
    labelKey: "sortBestPrice",
    icon: OfferIcon,
    activeClassName: "border-emerald-300 text-emerald-300",
    hoverClassName: "hover:border-emerald-400 hover:text-emerald-300",
  },
];

export function OfferSortSelector({
  value,
  onChange,
  getLinkTarget,
  className,
}: OfferSortSelectorProps) {
  const t = useTranslations("shared.affiliates");

  return (
    <div
      className={cn(
        "inline-grid h-11 w-full grid-cols-2 gap-1 rounded-md border border-border bg-secondary/50 p-1 sm:h-10 sm:w-auto",
        className,
      )}
    >
      {OPTIONS.map((option) => {
        const active = option.value === value;
        const Icon = option.icon;
        const optionClassName = cn(
          "inline-flex !h-full !w-full min-w-0 items-center justify-center gap-1 rounded border px-1.5 text-xs font-semibold transition-colors sm:min-w-[8.5rem] sm:gap-1.5 sm:px-2.5 sm:text-sm",
          active
            ? cn("bg-transparent shadow-sm", option.activeClassName)
            : cn(
                "border-transparent text-muted-foreground hover:bg-background/60",
                option.hoverClassName,
              ),
        );
        const content = (
          <>
            <Icon className="shrink-0 text-[17px]" />
            <span className="truncate">{t(option.labelKey)}</span>
          </>
        );

        if (getLinkTarget) {
          return (
            <Link
              key={option.value}
              {...getLinkTarget(option.value)}
              rel="nofollow"
              aria-current={active ? "page" : undefined}
              className={optionClassName}
              resetScroll={false}
            >
              {content}
            </Link>
          );
        }

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange?.(option.value)}
            className={optionClassName}
            aria-pressed={active}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
}
